// reusable presentational primitives — the building blocks every screen composes.

import { useEffect, useId, useRef, useState, type CSSProperties, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { Link, useLocation } from 'react-router-dom';
import { noteFor } from '../app/pageNotes.js';
import { useBackToClose, useIsCoarse, useIsPhone } from '../lib/media.js';
import { Logo3D } from './Logo3D.js';
import { IconChevronRight, IconClose, IconMore, IconSearch } from './icons.js';

// The loading indicator is the logo, spinning in 3D — shown whenever a screen is fetching.
export function Spinner() {
  return <Logo3D size={52} layers={11} depth={1.15} speed={2} className="spinner3d" />;
}

/** the logo spinning at the size of a line of text or a button: something loading or syncing right there.
 *  `decorative` when words beside it already say so ("Syncing…"). */
export function LogoSpin({ size = 16, label = 'Loading', decorative = false }: { size?: number; label?: string; decorative?: boolean }) {
  const small = size < 24;
  return <Logo3D size={size} layers={small ? 6 : 9} depth={small ? 0.55 : 0.9} speed={1.8} glow={false} className="logo-spin" label={label} decorative={decorative} />;
}

/** a Sync button's face: its icon (if any) and "Sync", or the logo spinning and "Syncing…" while it syncs */
export function SyncLabel({ busy, icon, idle = 'Sync', busyText = 'Syncing…' }: { busy: boolean; icon?: ReactNode; idle?: string; busyText?: string }) {
  return <>{busy ? <LogoSpin size={15} decorative /> : icon}{busy ? busyText : idle}</>;
}

/** a small loading line inside a panel, a popover or a list */
export function InlineLoading({ label = 'Loading…', className }: { label?: string; className?: string }) {
  return (
    <div className={`inline-loading${className ? ` ${className}` : ''}`} role="status">
      <LogoSpin size={20} decorative />
      <span>{label}</span>
    </div>
  );
}

export function LoadingState({ label = 'Loading…' }: { label?: string }) {
  return (
    <div className="state" role="status">
      <Spinner />
      <div className="muted">{label}</div>
    </div>
  );
}

export function EmptyState({ title, hint }: { title: string; hint?: string }) {
  return (
    <div className="state">
      <div className="big">{title}</div>
      {hint && <div className="muted">{hint}</div>}
    </div>
  );
}

export function ErrorState({ error, onRetry }: { error: unknown; onRetry?: () => void }) {
  const message = error instanceof Error ? error.message : 'Something went wrong';
  return (
    <div className="state error" role="alert">
      <div className="big">Error</div>
      <div className="muted">{message}</div>
      {onRetry && (
        <button className="btn btn-sm" onClick={onRetry}>
          Retry
        </button>
      )}
    </div>
  );
}

export function Pill({ kind, children, title }: { kind?: string; children: ReactNode; title?: string }) {
  return <span className={`pill ${kind ?? ''}`} title={title}>{children}</span>;
}

export function Tile({
  value,
  label,
  tone,
  accent,
}: {
  value: ReactNode;
  label: string;
  tone?: 'warn' | 'bad';
  accent?: boolean;
}) {
  return (
    <div className={`tile ${tone ?? ''}`}>
      <div className={`val ${accent ? 'accent' : ''}`}>{value}</div>
      <div className="lbl">{label}</div>
    </div>
  );
}

export function ProgressBar({ pct, done }: { pct: number; done?: boolean }) {
  const clamped = Math.max(0, Math.min(100, Math.round(pct)));
  return (
    <div className={`bar ${done ? 'done' : ''}`} title={`${clamped}%`}>
      {clamped > 0 && <span style={{ width: `${clamped}%` }} />}
    </div>
  );
}

// ── shared filter primitives (Service / Schedule / Overview share one look) ────────────────
/** a single toggleable filter pill, with an optional leading colour dot. */
export function Chip({
  active,
  onClick,
  dot,
  title,
  dashed,
  children,
  ariaLabel,
}: {
  active?: boolean;
  onClick?: () => void;
  /** CSS colour for a leading swatch (e.g. a task-type or status colour). */
  dot?: string;
  title?: string;
  dashed?: boolean;
  children: ReactNode;
  ariaLabel?: string;
}) {
  return (
    <button
      type="button"
      className={`fchip${active ? ' on' : ''}${dashed ? ' fchip-all' : ''}`}
      aria-pressed={!!active}
      aria-label={ariaLabel}
      title={title}
      onClick={onClick}
    >
      {dot && <span className="fchip-dot" style={{ background: dot }} />}
      {children}
    </button>
  );
}

/** a labelled cluster of chips inside a FilterBar. */
export function ChipGroup({ label, children }: { label?: string; children: ReactNode }) {
  return (
    <div className="fgroup">
      {label && <span className="fgroup-lbl">{label}</span>}
      {children}
    </div>
  );
}

/** a boolean filter toggle styled as a pill to match the chips (a checkbox tick + label). */
export function FilterToggle({ checked, onChange, title, children }: { checked: boolean; onChange: (v: boolean) => void; title?: string; children: ReactNode }) {
  return (
    <button
      type="button"
      className={`fchip fchip-toggle${checked ? ' on' : ''}`}
      aria-pressed={checked}
      title={title}
      onClick={() => onChange(!checked)}
    >
      <span className="fchk" aria-hidden="true" />
      {children}
    </button>
  );
}

/** a compact labelled dropdown filter ("Group: All ▾") — the professional, space-saving alternative to
 *  a long row of chips when a filter has many options. The FIRST option is treated as the default/"all"
 *  value, so the control shows an active tint whenever something else is picked. */
export function FilterSelect({ label, value, onChange, options, ariaLabel }: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  options: { value: string; label: string }[];
  ariaLabel?: string;
}) {
  const active = options.length > 0 && value !== options[0]!.value;
  return (
    <label className={`fsel${active ? ' on' : ''}`}>
      <span className="fsel-lbl">{label}</span>
      <select className="fsel-in" value={value} onChange={(e) => onChange(e.target.value)} aria-label={ariaLabel ?? label}>
        {options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
      </select>
      <span className="fsel-chev" aria-hidden="true">▾</span>
    </label>
  );
}

/** the search field with a leading icon and a clear (x) button when non-empty. `width` (px) is the
 *  desktop width; on phones the field fills its row. */
export function SearchInput({ value, onChange, placeholder, width }: { value: string; onChange: (v: string) => void; placeholder?: string; width?: number }) {
  const ref = useRef<HTMLInputElement>(null);
  return (
    <div className="fsearch" style={width ? ({ '--fsearch-w': `${width}px` } as CSSProperties) : undefined}>
      <IconSearch size={14} className="fsearch-ic" />
      <input
        ref={ref}
        className="field fsearch-in"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder ?? 'Search'}
        aria-label={placeholder ?? 'Search'}
      />
      {value && (
        <button type="button" className="fsearch-x" aria-label="Clear search" onClick={() => { onChange(''); ref.current?.focus(); }}>&#215;</button>
      )}
    </div>
  );
}

/** the shared filter bar: unframed, its controls laid straight on the page ground. When onClear is given
 *  and activeCount > 0 it appends a right-aligned "Clear filters (N)" button so every page resets the same way.
 *  Pass the search box as `search` (opt-in) and on phones the bar collapses to [search][Filters (n) ▾],
 *  with the other children and Clear opening below; wider screens lay out exactly as before:
 *    <FilterBar search={<SearchInput … />} activeCount={n} onClear={reset}>{chips…}</FilterBar>  */
export function FilterBar({ activeCount, onClear, search, children }: {
  activeCount?: number;
  onClear?: () => void;
  /** the always-visible search field, rendered first; turns on the phone "Filters (n)" disclosure. */
  search?: ReactNode;
  children: ReactNode;
}) {
  const n = activeCount ?? 0;
  const ref = useRef<HTMLDivElement>(null);
  const restId = useId();
  const isPhone = useIsPhone();
  const [open, setOpen] = useState(false);
  const collapsible = search != null && isPhone;
  const clear = onClear && n > 0 && (
    <button
      type="button"
      className="filterbar-clear"
      onClick={(e) => {
        onClear();
        // keyboard only (detail 0): the button unmounts, so hand focus to the first filter instead of <body>
        if (e.detail === 0) ref.current?.querySelector<HTMLElement>('input, select, button')?.focus();
      }}
    >Clear filters ({n})</button>
  );
  if (search == null) {
    return (
      <div className="filterbar" ref={ref}>
        {children}
        {clear}
      </div>
    );
  }
  return (
    <div className={`filterbar${collapsible ? ' filterbar--collapsible' : ''}`} ref={ref}>
      {search}
      {collapsible && (
        <button
          type="button"
          className={`filterbar-toggle${n > 0 ? ' on' : ''}`}
          aria-expanded={open}
          aria-controls={restId}
          onClick={() => setOpen((o) => !o)}
        >
          Filters
          {n > 0 && <span className="filterbar-badge" aria-label={`${n} active`}>{n}</span>}
          <IconChevronRight size={14} strokeWidth={2.2} className="filterbar-toggle-chev" />
        </button>
      )}
      <div className="filterbar-rest" id={restId} hidden={collapsible && !open}>
        {children}
        {clear}
      </div>
    </div>
  );
}

/** a segmented view switcher (the `.seg` control) with correct tab ARIA — role=tab + aria-selected on
 *  each option, so screen readers announce the active view. Replaces hand-rolled .seg markup. */
export function Segmented<T extends string>({ value, onChange, options, ariaLabel }: {
  value: T;
  onChange: (v: T) => void;
  options: { value: T; label: ReactNode; title?: string }[];
  ariaLabel?: string;
}) {
  // roving tabindex (APG tabs): only the selected tab is a Tab stop; Left/Right move + select.
  // falls back to the first tab when value matches none, so the control is never unreachable.
  const sel = Math.max(0, options.findIndex((o) => o.value === value));
  return (
    <div
      className="seg"
      role="tablist"
      aria-label={ariaLabel}
      onKeyDown={(e) => {
        if ((e.key !== 'ArrowRight' && e.key !== 'ArrowLeft') || !options.length) return;
        e.preventDefault();
        const j = (sel + (e.key === 'ArrowRight' ? 1 : -1) + options.length) % options.length;
        onChange(options[j]!.value);
        (e.currentTarget.children[j] as HTMLElement | undefined)?.focus();
      }}
    >
      {options.map((o, i) => (
        <button
          key={o.value}
          type="button"
          role="tab"
          aria-selected={value === o.value}
          tabIndex={i === sel ? 0 : -1}
          className={`seg-btn${value === o.value ? ' on' : ''}`}
          title={o.title}
          onClick={() => onChange(o.value)}
        >
          {o.label}
        </button>
      ))}
    </div>
  );
}

/** a compact, dismissable inline error for transient write/mutation failures. Unlike ErrorState it does
 *  not take over the page or shove content down — use ErrorState only for an initial-load failure. */
export function InlineError({ error, onDismiss }: { error: unknown; onDismiss?: () => void }) {
  const message = error instanceof Error ? error.message : 'Something went wrong';
  return (
    <div className="inline-error" role="alert">
      <span className="inline-error-ic" aria-hidden="true">!</span>
      <span className="inline-error-msg">{message}</span>
      {onDismiss && (
        <button type="button" className="inline-error-x" aria-label="Dismiss error" onClick={onDismiss}>&#215;</button>
      )}
    </div>
  );
}

interface ConfirmOpts {
  title: string;
  message?: ReactNode;
  confirmLabel?: string;
  cancelLabel?: string;
  danger?: boolean;
}
/** a themed replacement for window.confirm(): returns [node, confirm] — render `node`, then
 *  `if (await confirm({…})) …`. The dialog uses the shared Modal so it follows light/dark + palette. */
export function useConfirm(): [ReactNode, (opts: ConfirmOpts) => Promise<boolean>] {
  const [state, setState] = useState<(ConfirmOpts & { resolve: (v: boolean) => void }) | null>(null);
  const confirm = (opts: ConfirmOpts) =>
    new Promise<boolean>((resolve) => setState({ ...opts, resolve }));
  const close = (v: boolean) => {
    setState((s) => { s?.resolve(v); return null; });
  };
  const node = state ? (
    <Modal
      title={state.title}
      onClose={() => close(false)}
      footer={
        <>
          <button className="btn btn-ghost" onClick={() => close(false)}>{state.cancelLabel ?? 'Cancel'}</button>
          <button className={`btn ${state.danger ? 'btn-danger' : 'btn-primary'}`} onClick={() => close(true)}>{state.confirmLabel ?? 'Confirm'}</button>
        </>
      }
    >
      {state.message && <div className="confirm-msg">{state.message}</div>}
    </Modal>
  ) : null;
  return [node, confirm];
}

/** the page's title row: Archivo 28 title, then the page's controls and its "?" notes. `meta` adds the
 *  quiet line under the title ("Monday 5 October · season 26-27 · synced 08:42"); the controls then sit
 *  on that line's row. */
// ── fact sentence: the page's headline figures written as one sentence ─────────────────
// Replaces rows of big-number tiles: <FactSentence>Season 26-27 has <Fact to="/overview">14 boats</Fact>
// <FactEnd>.</FactEnd> …</FactSentence>. Each figure is a chip in the one status vocabulary (err / warn /
// info / ok / acc, or neutral); it can jump to a panel (onClick), open a page (to) or just read. Wrap
// trailing punctuation in FactEnd so it sits flush against its figure instead of after a gap.
export type FactTone = 'err' | 'warn' | 'info' | 'ok' | 'acc';
export function FactSentence({ children, className, sub }: { children: ReactNode; className?: string; sub?: boolean }) {
  return <p className={`${sub ? 'facts-sub' : 'facts'}${className ? ` ${className}` : ''}`}>{children}</p>;
}
export function Fact({ tone, icon, to, onClick, pressed, title, children }: {
  tone?: FactTone;
  icon?: ReactNode;
  /** open a page */
  to?: string;
  /** e.g. jump to the panel the figure counts */
  onClick?: () => void;
  /** with onClick: the figure is a toggle (e.g. shows / hides its rows), announced as aria-pressed */
  pressed?: boolean;
  title?: string;
  children: ReactNode;
}) {
  const cls = `fact${tone ? ` ${tone}` : ''}`;
  if (to) return <Link className={cls} to={to} title={title}>{icon}{children}</Link>;
  if (onClick) return <button type="button" className={cls} title={title} aria-pressed={pressed} onClick={onClick}>{icon}{children}</button>;
  return <span className={cls} title={title}>{icon}{children}</span>;
}
/** punctuation that follows a Fact chip: kept on its line and tucked against the chip. */
export const FactEnd = ({ children }: { children: ReactNode }) => <span className="fact-end">{children}</span>;

export function PageHeader({ title, meta, children }: { title: string; meta?: ReactNode; children?: ReactNode }) {
  return (
    <div className="page-head">
      {meta != null ? (
        <div className="page-titles">
          <h1 className="page-title">{title}</h1>
          <p className="page-sub">{meta}</p>
        </div>
      ) : (
        <h1 className="page-title">{title}</h1>
      )}
      <div className="spacer" />
      {children}
      <PageHelpButton />
    </div>
  );
}

/** the "?" help button for the current route, resolved from pageNotes. Renders nothing when the
 *  page has no note. Exported so pages with a custom header bar (embeds, hubs) can drop it in too. */
export function PageHelpButton() {
  const { pathname } = useLocation();
  const note = noteFor(pathname);
  if (!note) return null;
  return <PageNotesButton note={note} />;
}

/** after a touch tap has dismissed a popover, eat the click that tap is about to fire, so it doesn't
 *  also press whatever sits underneath. Mouse clicks keep their desktop behaviour (close + act). A press
 *  that turns into a scroll (pointercancel) fires no click, so it disarms instead of eating the next tap. */
function swallowNextClick(pointerId: number) {
  const stop = (e: Event) => { e.preventDefault(); e.stopPropagation(); done(); };
  const cancel = (e: PointerEvent) => { if (e.pointerId === pointerId) done(); };
  const done = () => {
    document.removeEventListener('click', stop, true);
    document.removeEventListener('pointercancel', cancel, true);
    window.clearTimeout(timer);
  };
  document.addEventListener('click', stop, true);
  document.addEventListener('pointercancel', cancel, true);
  const timer = window.setTimeout(done, 700);
}

/** the "?" in a page header: a small anchored popover of notes for the current page. On phones the notes
 *  open as a bottom sheet instead, portalled to <body> so a glass toolbar's backdrop-filter (Explore and
 *  the hubs) cannot trap the fixed layer. */
function PageNotesButton({ note }: { note: { title: string; body: string[] } }) {
  const [open, setOpen] = useState(false);
  const isPhone = useIsPhone();
  const coarse = useIsCoarse();
  const ref = useRef<HTMLDivElement>(null);
  const close = () => setOpen(false);
  const asPopover = open && !isPhone;
  // the sheet brings its own back-gesture, scrim and Escape handling (Modal); the popover wires its own
  useBackToClose(asPopover, close);
  useEffect(() => {
    if (!asPopover) return;
    const onDown = (e: PointerEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
        if (e.pointerType !== 'mouse') swallowNextClick(e.pointerId);
      }
    };
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('pointerdown', onDown);
    document.addEventListener('keydown', onKey);
    return () => { document.removeEventListener('pointerdown', onDown); document.removeEventListener('keydown', onKey); };
  }, [asPopover]);
  return (
    <div className="help-wrap" ref={ref}>
      <button
        className={`help-btn ${open ? 'open' : ''}`}
        onClick={() => setOpen((o) => !o)}
        aria-label="Notes for this page"
        aria-expanded={open}
        title="Notes for this page"
      >?</button>
      {asPopover && (
        <div className="help-pop" role="dialog" aria-label={`${note.title} notes`}>
          <div className="help-pop-hd">
            {note.title}
            {coarse && (
              <button type="button" className="help-pop-x" aria-label="Close notes" onClick={close}>
                <IconClose size={18} />
              </button>
            )}
          </div>
          {note.body.map((line, i) => <p key={i}>{line}</p>)}
        </div>
      )}
      {open && isPhone && createPortal(
        <Modal sheet title={note.title} onClose={close} closeLabel="Close notes">
          <div className="help-sheet">
            {note.body.map((line, i) => <p key={i}>{line}</p>)}
          </div>
        </Modal>,
        document.body,
      )}
    </div>
  );
}

// Greek (and polytonic Greek) letters: such a title is tagged lang="el", so the uppercase header drops the
// tonos the way Greek capitals are written ('ΕΛΕΝΗ', not 'ΕΛΈΝΗ')
const GREEK = /[\u0370-\u03ff\u1f00-\u1fff]/;

/** the shared dialog. Centred card on desktop; on phones (and with `sheet`, at every width) a bottom sheet
 *  with a scrolling body, a pinned footer of equal-width buttons, a 44px close x and safe-area padding,
 *  riding above the iOS keyboard (--kb-inset). On touch, the back gesture closes it (useBackToClose is
 *  wired here, so callers must not add their own for a Modal). */
export function Modal({
  title,
  onClose,
  children,
  footer,
  width,
  sheet,
  dataTitle,
  closeLabel = 'Close',
  className,
}: {
  title: string;
  onClose: () => void;
  children: ReactNode;
  footer?: ReactNode;
  /** opt-in wider dialog (px) for form-heavy modals; the body scrolls so the footer stays pinned. */
  width?: number;
  /** present as a bottom sheet at every width (account panel, notes, cell history, "more" menus);
   *  every Modal already becomes a sheet on phones. */
  sheet?: boolean;
  /** the title is a record's name (a customer, a person): keep its own casing instead of uppercasing. */
  dataTitle?: boolean;
  /** accessible name of the header close x (shown on phones, touch screens and sheets). */
  closeLabel?: string;
  /** extra class on the .modal box, for a page's own sizing (e.g. a full-height viewer on phones). */
  className?: string;
}) {
  const wide = width != null;
  // remember the opener while rendering, before any autoFocus inside the dialog runs
  const [opener] = useState(() => document.activeElement as HTMLElement | null);
  const dlg = useRef<HTMLDivElement>(null);
  // a press that starts inside (e.g. drag-selecting in a field) and ends on the scrim must not close
  const downOnScrim = useRef(false);
  // move focus into the dialog (unless a field inside already took it) and hand it back on close, unless
  // the opener has left the page meanwhile (focusing a detached node would drop focus to <body> anyway)
  useEffect(() => {
    if (!dlg.current?.contains(document.activeElement)) dlg.current?.focus();
    return () => { if (opener?.isConnected) opener.focus?.(); };
  }, [opener]);
  useBackToClose(true, onClose); // mounted only while open; a no-op on fine pointers
  const boxClass = `modal${wide ? ' modal--wide' : ''}${className ? ` ${className}` : ''}`;
  return (
    <div
      className={`modal-bg${sheet ? ' modal-bg--sheet' : ''}`}
      onMouseDown={(e) => { downOnScrim.current = e.target === e.currentTarget; }}
      onClick={(e) => { if (downOnScrim.current && e.target === e.currentTarget) onClose(); }}
      onKeyDown={(e) => { if (e.key === 'Escape' && !e.defaultPrevented) { e.stopPropagation(); onClose(); } }}
    >
      <div
        ref={dlg}
        tabIndex={-1}
        className={boxClass}
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-label={title}
        style={wide ? ({ '--modal-w': `${width}px` } as CSSProperties) : undefined}
      >
        <div className="modal-hd">
          <h3 lang={GREEK.test(title) ? 'el' : undefined} className={dataTitle ? 'is-data' : undefined}>{title}</h3>
          <button type="button" className="modal-close" aria-label={closeLabel} onClick={onClose}>
            <IconClose size={20} />
          </button>
        </div>
        <div className="modal-bd">{children}</div>
        {footer && <div className="modal-ft">{footer}</div>}
      </div>
    </div>
  );
}

/** a sticky bottom bar for a page's commit actions (Save, Notify, Print (n), Confirm), within thumb reach
 *  on phones. Render it as the LAST child of the page root; it bleeds to the screen edges and clears the
 *  home indicator. Primary action last (right).
 *    <ActionBar show={dirty} message={`${n} unsaved`}>
 *      <button className="btn btn-ghost" onClick={discard}>Discard</button>
 *      <button className="btn btn-primary" onClick={save}>Save changes</button>
 *    </ActionBar>
 *  phoneOnly (default) hides it above the PHONE tier, where the page keeps its header buttons. */
export function ActionBar({ show = true, phoneOnly = true, message, className, children }: {
  /** render nothing when false (e.g. nothing selected, nothing dirty). */
  show?: boolean;
  /** hidden above the PHONE tier (default). Pass false and show it wider through your own class. */
  phoneOnly?: boolean;
  /** the status text on the left ("3 unsaved", "2 selected"); announced politely. */
  message?: ReactNode;
  className?: string;
  children?: ReactNode;
}) {
  if (!show) return null;
  return (
    <div className={`action-bar${phoneOnly ? ' action-bar--phone' : ''}${className ? ` ${className}` : ''}`}>
      {message != null && <div className="action-bar-msg" aria-live="polite">{message}</div>}
      {children}
    </div>
  );
}

export interface OverflowAction {
  label: string;
  icon?: ReactNode;
  onClick?: () => void;
  /** renders a link instead of a button (a download, an export URL). */
  href?: string;
  download?: string | boolean;
  target?: string;
  disabled?: boolean;
  /** destructive: red row in the phone sheet. */
  danger?: boolean;
  title?: string;
  /** the inline button's class above the PHONE tier; default 'btn btn-sm', as pages render them today. */
  className?: string;
}

/** a page's secondary actions (Excel, PDF, Print, Sync, CSV, Import). Above the PHONE tier they render
 *  inline exactly as before: one button per item, same label, class and order. On phones they fold into
 *  one 44px ⋯ button that opens a bottom sheet of 52px rows and closes after the action. Falsy items are
 *  skipped, so `can('x') && {…}` works. A file input's action passes an onClick that clicks the input.
 *    <OverflowActions items={[{ label: 'Excel', icon: <IconDownload size={13} />, onClick: exportXlsx },
 *                             { label: 'Sync', onClick: sync, disabled: syncing }]} />  */
export function OverflowActions({ items, label = 'More actions', title = 'Actions' }: {
  items: (OverflowAction | false | null | undefined)[];
  /** accessible name of the ⋯ button */
  label?: string;
  /** the sheet's title */
  title?: string;
}) {
  const isPhone = useIsPhone();
  const [open, setOpen] = useState(false);
  const trigger = useRef<HTMLButtonElement>(null);
  const list = items.filter((it): it is OverflowAction => !!it);
  if (!list.length) return null;
  if (!isPhone) {
    return (
      <>
        {list.map((it, i) => {
          const cls = it.className ?? 'btn btn-sm';
          return it.href ? (
            <a key={i} className={cls} href={it.href} download={it.download} target={it.target}
              rel={it.target === '_blank' ? 'noopener noreferrer' : undefined} title={it.title}
              aria-disabled={it.disabled || undefined}
              onClick={it.disabled ? (e) => e.preventDefault() : it.onClick}>
              {it.icon}{it.label}
            </a>
          ) : (
            <button key={i} type="button" className={cls} onClick={it.onClick} disabled={it.disabled} title={it.title}>
              {it.icon}{it.label}
            </button>
          );
        })}
      </>
    );
  }
  // focus the ⋯ first: a dialog the action opens records the trigger as its opener, not this sheet's row
  // (unmounted a moment later), so closing that dialog hands focus back to the ⋯
  const run = (it: OverflowAction) => {
    trigger.current?.focus({ preventScroll: true });
    it.onClick?.();
    setOpen(false);
  };
  return (
    <>
      <button ref={trigger} type="button" className="btn btn-sm ovf-trigger" aria-label={label} title={label}
        aria-haspopup="dialog" aria-expanded={open} onClick={() => setOpen(true)}>
        <IconMore size={18} />
      </button>
      {open && createPortal(
        <Modal sheet title={title} onClose={() => setOpen(false)}>
          <div className="ovf-list">
            {list.map((it, i) => {
              const cls = `ovf-row${it.danger ? ' danger' : ''}`;
              const body = (
                <>
                  <span className="ovf-row-ic" aria-hidden="true">{it.icon}</span>
                  <span>{it.label}</span>
                </>
              );
              return it.href && !it.disabled ? (
                <a key={i} className={cls} href={it.href} download={it.download} target={it.target}
                  rel={it.target === '_blank' ? 'noopener noreferrer' : undefined} title={it.title} onClick={() => run(it)}>
                  {body}
                </a>
              ) : (
                <button key={i} type="button" className={cls} disabled={it.disabled} title={it.title} onClick={() => run(it)}>
                  {body}
                </button>
              );
            })}
          </div>
        </Modal>,
        document.body,
      )}
    </>
  );
}
